// 템플릿 전역 속성 — 「모사 데이터」 구획(2026-09-03 결정, 2026-09-04 성격 분리로 확장).
//
// 데이터 모사 한 벌씩을 **프리셋**으로 만들어 템플릿과 함께 저장한다. 종전에는 모사 값이 실데이터
// 반영 모달에만 있어 해제·템플릿 변경 시 소실됐다 — 여기서 만든 프리셋은 템플릿 자산
// (`ReportTemplate.simulationPresets`)이라 저장 버튼으로 함께 영속된다.
//
// 2026-09-04 성격 분리(모달=실데이터, 속성 패널=모사) 이후 **적용/해제도 이 구획 몫**이다:
//   - 적용은 대체 의미론(`applyPresetToSimulation` 헤더가 정본 — plan 축 값은 유지).
//   - **적용 중 프리셋의 인라인 수정은 미리보기에 즉시 반영**된다(즉석 모사가 이 흐름으로
//     흡수 — 프리셋 하나 만들어 바로 쓰면 일회성 확인도 된다). 반영 자체는 호스트(EditorPage)가
//     onChange에서 진다 — 이 구획은 목록·기본 지정을 한 번에 보고할 뿐이다.
//   - **기본 지정**(상호배타 체크박스, 해제 가능)은 `ReportTemplate.defaultSimulationPresetId` —
//     로드 시마다 그 프리셋이 미리보기 모사에 반영된다(결정 §4).
//   - 보고서 연결 중(`linkedReportActive`)이면 서버가 모사를 무시하므로 구획 전체를 비활성한다
//     (fieldset — 막기만 하면 "고쳤는데 안 바뀐다"로 보이니 문구가 이유를 말한다).
//
// 축 후보는 실데이터 반영 모달과 같은 파생(`derivePreviewSimulationAxes`) 결과를 받되,
// **plan 축(분할 그룹)은 편집 후보에서 아예 제외**한다(`isPresetStorableAxis`) — 그 값(행 id)은
// 점검·시점 종속이라 템플릿 자산으로 박제하면 반드시 썩는다(결정 §plan 축 제외).
// 행 편집기는 모달의 것(SimulationAxisRow)을 공유한다 — 같은 축이 호스트마다 다른 위젯으로
// 보이면 안 된다.

import { useEffect, useRef, useState } from 'react';
import type {
  PreviewSimulationAxis,
  PreviewSimulationOption,
  SimulationPreset,
} from '@safetyhub/shared';
import { Tooltip } from '../../components/Tooltip';
import { INPUT_CLS } from './editorFieldClasses';
import {
  parseSimulationAxisInput,
  setSimulationAxisValue,
} from './previewSimulationAxes';
import { isPresetStorableAxis } from './simulationPresets';
import { SimulationAxisRow } from './SimulationAxisRow';

/** cc- 계열과 같은 방식의 프리셋 id — crypto.randomUUID 우선, 폴백은 시간+랜덤. */
function newPresetId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ?? `preset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** 목록+기본 지정을 **한 덩어리로** 보고 — 삭제가 기본 해제를 동반해도 호스트 apply 1회(undo 1스텝). */
export interface SimulationPresetsChange {
  presets: SimulationPreset[];
  defaultPresetId: string | undefined;
}

interface Props {
  presets: SimulationPreset[] | undefined;
  /** 모사 축 후보 전체(파생 결과 그대로) — plan 축 필터는 이 구획이 진다. */
  axes: PreviewSimulationAxis[];
  /**
   * 프리셋 목록·기본 지정 갱신 — 템플릿 draft에 반영돼 dirty에 합류한다(저장 버튼으로 함께
   * 저장). 적용 중 프리셋이 수정됐으면 호스트가 미리보기 재반영까지 진다(즉시 반영 규약).
   */
  onChange: (next: SimulationPresetsChange) => void;
  /** 기본 프리셋 id(template draft) — 상호배타 체크박스의 값. */
  defaultPresetId: string | undefined;
  /** 지금 미리보기에 적용 중인 프리셋 id(호스트 소유 — 인라인 수정 즉시 반영의 대상). */
  appliedPresetId: string | null;
  /** 프리셋 적용(대체 — plan 축 값 유지). */
  onApply: (preset: SimulationPreset) => void;
  /** 적용 해제(storable 축 전부 걷기 — plan 축 값 유지). */
  onUnapply: () => void;
  /** 보고서 연결 중 — 서버가 모사를 무시하므로 구획 전체 비활성 + 문구. */
  linkedReportActive?: boolean;
}

export function SimulationPresetsSection({
  presets,
  axes,
  onChange,
  defaultPresetId,
  appliedPresetId,
  onApply,
  onUnapply,
  linkedReportActive = false,
}: Props) {
  const list = presets ?? [];
  const storableAxes = axes.filter(isPresetStorableAxis);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 값을 아직 안 고른 채 추가만 한 축(값이 생기면 목록 파생이 알아서 잡는다 — 모달과 같은 규약).
  const [addedAxisKeys, setAddedAxisKeys] = useState<string[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef<HTMLDivElement>(null);

  const selected = list.find((p) => p.id === selectedId) ?? null;

  // 프리셋 전환 시 빈 행 잔존 정리 — 다른 프리셋의 "추가만 한 축"이 따라오면 안 된다.
  const prevSelectedIdRef = useRef(selectedId);
  if (prevSelectedIdRef.current !== selectedId) {
    prevSelectedIdRef.current = selectedId;
    if (addedAxisKeys.length > 0) setAddedAxisKeys([]);
  }

  // 후보 추가 드롭다운 — 바깥 클릭 / Esc → 닫기(모달의 추가 드롭다운과 같은 동작).
  useEffect(() => {
    if (!addOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (addRef.current && !addRef.current.contains(e.target as Node)) setAddOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAddOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [addOpen]);

  // 갱신 — nextDefault는 **명시 2-인자**다(기본 매개변수 금지): `emit(list, undefined)`처럼
  // "해제"를 넘겨도 default parameter는 undefined에 발동해 현재 값으로 조용히 되살린다
  // (merge 재검증에서 재클릭 해제·삭제 동반 해제 spec 2건이 이 경로로 실패 — 실측 적발).
  const emit = (nextPresets: SimulationPreset[], nextDefault: string | undefined) =>
    onChange({ presets: nextPresets, defaultPresetId: nextDefault });
  /** 목록만 바뀌는 갱신 — 기본 지정은 현재 값 유지. */
  const emitPresets = (nextPresets: SimulationPreset[]) => emit(nextPresets, defaultPresetId);

  const updatePreset = (id: string, patch: (p: SimulationPreset) => SimulationPreset) => {
    emitPresets(list.map((p) => (p.id === id ? patch(p) : p)));
  };

  const handleAddPreset = () => {
    const id = newPresetId();
    emitPresets([...list, { id, name: `프리셋 ${list.length + 1}`, values: {} }]);
    setSelectedId(id);
  };

  // 삭제 — 그 id가 기본이면 **함께 해제**한다(결정 §4: 없는 프리셋을 가리키는 기본은 로드마다
  // 조용히 no-op이 되는 유령이다). 두 축을 한 emit으로 보내 호스트 apply 1회(undo 1스텝).
  const handleRemovePreset = (id: string) => {
    emit(
      list.filter((p) => p.id !== id),
      defaultPresetId === id ? undefined : defaultPresetId,
    );
    if (selectedId === id) setSelectedId(null);
  };

  // 기본 지정 토글 — 상호배타(하나 체크 시 다른 것 해제) + 재클릭으로 해제 가능(라디오가 아니라
  // 체크박스인 이유 — 결정 §검토한 대안).
  const handleToggleDefault = (id: string) => {
    emit(list, defaultPresetId === id ? undefined : id);
  };

  /** 축 값 설정/해제 — values와 labels를 함께 관리(값 없는 라벨은 남기지 않는다). */
  const setAxisValue = (axis: PreviewSimulationAxis, value: unknown, label?: string) => {
    if (!selected) return;
    updatePreset(selected.id, (p) => {
      const values = setSimulationAxisValue(p.values, axis.key, value) ?? {};
      const labels = { ...p.labels };
      if (value === undefined || label === undefined) delete labels[axis.key];
      else labels[axis.key] = label;
      return {
        ...p,
        values,
        ...(Object.keys(labels).length > 0 ? { labels } : { labels: undefined }),
      };
    });
  };

  const handleSelectOption = (
    axis: PreviewSimulationAxis,
    option: PreviewSimulationOption | undefined,
  ) => setAxisValue(axis, option?.value, option?.label);

  const handleTypeValue = (axis: PreviewSimulationAxis, raw: string) => {
    const parsed = parseSimulationAxisInput(axis, raw);
    if (parsed.kind === 'incomplete') return;
    if (parsed.kind === 'unset') setAxisValue(axis, undefined);
    else setAxisValue(axis, parsed.value, raw);
  };

  const handleRemoveAxis = (axis: PreviewSimulationAxis) => {
    setAddedAxisKeys((keys) => keys.filter((k) => k !== axis.key));
    setAxisValue(axis, undefined);
  };

  // 화면에 뜨는 행 = 후보 순서대로, "추가했거나 값이 있는" 축(모달과 같은 파생).
  const activeAxes = selected
    ? storableAxes.filter(
        (axis) =>
          addedAxisKeys.includes(axis.key) || selected.values[axis.key] !== undefined,
      )
    : [];
  const candidateAxes = storableAxes.filter(
    (axis) => !activeAxes.some((a) => a.key === axis.key),
  );

  return (
    <div className="pt-2 border-t border-gray-100">
      <span className="text-xs font-medium text-gray-600">모사 데이터</span>
      <p className="mt-1 text-[11px] text-gray-400">
        데이터 모사 한 벌씩을 프리셋으로 만들어 템플릿과 함께 저장합니다. 실데이터 반영 모달에서
        골라 반영합니다. 분할 그룹은 점검마다 달라 프리셋에 담기지 않습니다(적용 시점에 고릅니다).
      </p>
      {/* 프리셋이 템플릿과 함께 "저장"되다 보니 실제 보고서에 들어가는 값으로 오해될 수 있다
          (2026-09-03 사용자 지적) — 저장되는 것은 정의뿐이고 값은 저작 미리보기에만 걸린다. */}
      <p className="mt-1 text-[11px] text-amber-600">
        모사 값은 Composer 저작 화면의 확인 용도로만 쓰입니다 — 실제 보고서 생성·내용에는 들어가지
        않습니다.
      </p>
      {/* 배타(결정 2026-09-04 §3) — 보고서 연결 중이면 서버가 모사를 무시한다. 아래 fieldset이
          조작을 막고, 이 문구가 이유를 말한다(막기만 하면 "왜 안 되지"만 남는다). */}
      {linkedReportActive && (
        <p className="mt-1 text-[11px] text-amber-600">
          실제 보고서 연결 중 — 모사가 반영되지 않습니다. 모사를 쓰려면 실데이터 반영 모달에서
          보고서 연결을 해제하세요.
        </p>
      )}
      {storableAxes.length === 0 ? (
        <p className="mt-2 text-[11px] text-gray-400">
          이 양식에는 모사할 수 있는 데이터 축이 없습니다.
        </p>
      ) : (
        <fieldset
          disabled={linkedReportActive}
          className={linkedReportActive ? 'opacity-60' : undefined}
        >
          <ul className="mt-2 flex flex-col gap-1">
            {list.map((preset) => {
              const isSelected = preset.id === selectedId;
              const isApplied = preset.id === appliedPresetId;
              const isDefault = preset.id === defaultPresetId;
              const valueCount = Object.keys(preset.values).length;
              return (
                <li key={preset.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setSelectedId(isSelected ? null : preset.id)}
                    aria-expanded={isSelected}
                    className={`flex-1 min-w-0 truncate rounded border px-2 py-1 text-left text-xs ${
                      isSelected
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                    }`}
                  >
                    {preset.name}
                    <span className="ml-1 text-[10px] text-gray-400">값 {valueCount}개</span>
                  </button>
                  {/* 적용/해제(2026-09-04 성격 분리 — 모달의 「프리셋 적용…」 select 대체).
                      적용은 상태다: 적용 중 프리셋의 인라인 수정이 미리보기에 즉시 반영되는
                      대상이라, 어느 벌이 걸려 있는지 화면이 계속 말해야 한다(§⑥). */}
                  <button
                    type="button"
                    onClick={() => (isApplied ? onUnapply() : onApply(preset))}
                    aria-pressed={isApplied}
                    className={`shrink-0 rounded border px-1.5 py-1 text-[11px] ${
                      isApplied
                        ? 'border-brand-400 bg-brand-100 font-medium text-brand-700'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {isApplied ? '적용 중' : '적용'}
                  </button>
                  {/* 기본 지정 — 상호배타 체크박스(하나만·재클릭 해제, 라디오는 해제가 안 된다).
                      값은 template draft의 defaultSimulationPresetId — 로드 시마다 반영(결정 §4). */}
                  <label className="flex shrink-0 cursor-pointer items-center gap-0.5 text-[11px] text-gray-500">
                    <input
                      type="checkbox"
                      checked={isDefault}
                      onChange={() => handleToggleDefault(preset.id)}
                      aria-label={`${preset.name} 프리셋을 기본으로 적용`}
                      className="h-3 w-3"
                    />
                    기본
                  </label>
                  <button
                    type="button"
                    onClick={() => handleRemovePreset(preset.id)}
                    className="shrink-0 rounded px-1 text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label={`${preset.name} 프리셋 삭제`}
                  >
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            onClick={handleAddPreset}
            className="mt-1 rounded border border-dashed border-gray-300 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50"
          >
            ＋ 프리셋 추가
          </button>

          {selected && (
            <div className="mt-2 flex flex-col gap-1.5 rounded border border-gray-200 bg-gray-50/50 p-2">
              <label className="flex items-center gap-2 text-[11px] text-gray-500">
                <span className="shrink-0">이름</span>
                <input
                  type="text"
                  value={selected.name}
                  onChange={(e) =>
                    updatePreset(selected.id, (p) => ({ ...p, name: e.target.value }))
                  }
                  className={INPUT_CLS}
                />
              </label>
              {activeAxes.map((axis) => (
                <SimulationAxisRow
                  key={axis.key}
                  axis={axis}
                  current={selected.values[axis.key]}
                  options={axis.options}
                  storedLabel={selected.labels?.[axis.key]}
                  radioNamePrefix={`composer-sim-preset-${selected.id}`}
                  onSelectOption={(opt) => handleSelectOption(axis, opt)}
                  onTypeValue={(raw) => handleTypeValue(axis, raw)}
                  onRemove={() => handleRemoveAxis(axis)}
                />
              ))}
              {candidateAxes.length > 0 && (
                <div ref={addRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setAddOpen((o) => !o)}
                    className="rounded border border-dashed border-gray-300 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50"
                    aria-haspopup="listbox"
                    aria-expanded={addOpen}
                  >
                    ＋ 모사 값 추가
                  </button>
                  {addOpen && (
                    <div
                      role="listbox"
                      className="absolute z-30 mt-0.5 max-h-60 w-72 overflow-auto rounded border border-gray-300 bg-white px-1 py-0.5 shadow-lg"
                    >
                      {candidateAxes.map((axis) => (
                        // 설명 툴팁 — 실데이터 반영 모달의 후보 목록과 같은 어포던스(같은 축이
                        // 호스트마다 다르게 보이면 안 된다). 내용은 파생이 채운 axis.description.
                        <Tooltip key={axis.key} content={axis.description}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={false}
                            onClick={() => {
                              setAddOpen(false);
                              setAddedAxisKeys((keys) =>
                                keys.includes(axis.key) ? keys : [...keys, axis.key],
                              );
                            }}
                            className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-xs hover:bg-gray-50"
                          >
                            <span className="truncate">{axis.label}</span>
                            <span className="ml-auto shrink-0 font-mono text-[10px] text-gray-400">
                              {axis.key}
                            </span>
                          </button>
                        </Tooltip>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
}

